import type {
  GridDataSourceCache,
  GridGetRowsParams,
  GridGetRowsResponse,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import { z } from 'zod';

/**
 * Schema for the JSON-serialized cache key, used to derive the `CacheKey`
 * type that `getKey` builds. `groupKeys` is part of the key identity so each
 * group path caches under a distinct entry.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars -- referenced only via `z.infer<typeof …>`
const cacheKeySchema = z.object({
  end: z.union([z.number(), z.string()]),
  filterModel: z.unknown(),
  groupKeys: z.array(z.string()).optional(),
  sortModel: z.unknown(),
  start: z.union([z.number(), z.string()]),
});

type CacheKey = z.infer<typeof cacheKeySchema>;

/**
 * DataGrid data-source cache that supports in-place row updates.
 * Instead of clearing the entire cache when Apollo pushes updated rows,
 * this patches the cached GridGetRowsResponse entries so stale data
 * never resurfaces when the DataGrid re-reads from cache.
 */
export class ReportDataSourceCache implements GridDataSourceCache {
  private cache = new Map<string, GridGetRowsResponse>();
  /** Reverse index: row id → set of cache keys containing that row */
  private rowKeyIndex = new Map<string, Set<string>>();

  private getKey(params: GridGetRowsParams) {
    const key: CacheKey = {
      end: params.end,
      filterModel: params.filterModel,
      groupKeys: params.groupKeys,
      sortModel: params.sortModel,
      start: params.start,
    };
    return JSON.stringify(key);
  }

  set(key: GridGetRowsParams, value: GridGetRowsResponse) {
    const cacheKey = this.getKey(key);
    this.cache.set(cacheKey, value);
    for (const row of value.rows) {
      const rowId = String(row.id);
      const keys = this.rowKeyIndex.get(rowId);
      if (keys) keys.add(cacheKey);
      else this.rowKeyIndex.set(rowId, new Set([cacheKey]));
    }
  }

  get(key: GridGetRowsParams) {
    return this.cache.get(this.getKey(key));
  }

  clear() {
    this.cache = new Map();
    this.rowKeyIndex = new Map();
  }

  /**
   * Patches only the cache entries that contain affected rows.
   * Uses a reverse index to skip entries with no matching rows.
   */
  updateCachedRows(updatedRows: GridValidRowModel[]) {
    if (!updatedRows.length) return;

    const affectedKeys = new Set<string>();
    const updatesById = new Map<string, GridValidRowModel>();
    for (const row of updatedRows) {
      const rowId = String(row.id);
      updatesById.set(rowId, row);
      const keys = this.rowKeyIndex.get(rowId);
      keys?.forEach(k => affectedKeys.add(k));
    }

    for (const cacheKey of affectedKeys) {
      const entry = this.cache.get(cacheKey);
      if (!entry) continue;
      this.cache.set(cacheKey, {
        ...entry,
        rows: entry.rows.map(row => {
          const update = updatesById.get(String(row.id));
          return update ? { ...row, ...update } : row;
        }),
      });
    }
  }

  /**
   * Surgically removes rows by id from every cached chunk that contains
   * them, without deleting the chunk entries themselves — the chunk's
   * `GridGetRowsParams` key must still resolve if MUI queries it again, just
   * with the trashed rows stripped out. Mirrors the reverse-index pattern
   * used by {@link updateCachedRows} so we only touch chunks that matter.
   *
   * Preferred over {@link clear} on mutations that only remove a handful of
   * rows: clearing drops accumulated row counts and forces the
   * viewport-driven lazy loader to chase the user's scroll position.
   */
  removeRows(rowIds: readonly string[]) {
    if (!rowIds.length) return;

    const removedSet = new Set(rowIds.map(String));
    const affectedKeys = new Set<string>();
    for (const rowId of removedSet) {
      const keys = this.rowKeyIndex.get(rowId);
      keys?.forEach(k => affectedKeys.add(k));
    }

    for (const cacheKey of affectedKeys) {
      const entry = this.cache.get(cacheKey);
      if (!entry) continue;
      this.cache.set(cacheKey, {
        ...entry,
        rows: entry.rows.filter(row => !removedSet.has(String(row.id))),
      });
    }

    for (const rowId of removedSet) this.rowKeyIndex.delete(rowId);
  }
}
